/* ════════════════════════════════════════════════════════════════════════
   BARCODE NUTRITION UTILS — local product lookup + safe API hook surface.
   NO direct frontend calls to private barcode/LLM APIs. Pure helpers only.
   ════════════════════════════════════════════════════════════════════════ */

import { PRODUCT_BARCODE_INDEX } from "../data/productNutritionMockData";

/* Strip whitespace + non-digits and collapse to a canonical EAN/UPC string. */
export function normaliseBarcode(raw) {
  if (raw == null) return "";
  return String(raw).replace(/\D/g, "").trim();
}

/* Lookup a product in the local mock table. Returns the product or null. */
export function lookupProductByBarcodeLocal(code) {
  const key = normaliseBarcode(code);
  if (!key) return null;
  return PRODUCT_BARCODE_INDEX[key] || null;
}

/* Future-API hook. Always falls back to local lookup until a secure backend
   barcode lookup exists. Never calls private APIs directly from the browser. */
export async function lookupProductByBarcodeFutureApi(code) {
  // TODO: when a secure backend endpoint exists, call it here. Until then,
  // mirror the local lookup so callers don't branch.
  return lookupProductByBarcodeLocal(code);
}

/* Map a scanned product → ingredient row used by the recipe form. */
export function mapProductNutritionToIngredient(product, { quantity = 1, unit = null, note = "" } = {}) {
  if (!product) return null;
  const macros = calculateIngredientMacrosFromServing(product, { quantity, unit });
  return {
    raw: `${quantity}${unit ? ` ${unit}` : ""} ${product.productName}`.trim(),
    ingredientName: product.productName,
    brand: product.brand || "",
    barcode: product.barcode || "",
    quantity,
    unit: unit || product.servingUnit || "serving",
    calories: macros.calories,
    protein:  macros.protein,
    carbs:    macros.carbs,
    fat:      macros.fat,
    note,
    source: "barcode-scan",
    confidence: "Good",
  };
}

/* Compute approximate macros for a user-chosen quantity. If the unit matches
   the product's serving unit, scale linearly. If the unit is g/ml and we have
   nutritionPer100g we use that; otherwise fall back to one serving × qty. */
export function calculateIngredientMacrosFromServing(product, { quantity = 1, unit = null } = {}) {
  const ZERO = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  if (!product) return ZERO;
  const q = Number(quantity) || 0;
  if (q <= 0) return ZERO;

  const u = (unit || product.servingUnit || "serving").toLowerCase();
  const su = (product.servingUnit || "").toLowerCase();

  // Direct match: e.g. user picks "g" and product is "g".
  if (u === su || u === "serving") {
    const factor = u === "serving" ? q : (q / (product.servingSize || 1));
    return scaleMacros(product.nutritionPerServe, factor);
  }
  // g/ml with per-100g table available.
  if ((u === "g" || u === "ml") && product.nutritionPer100g) {
    return scaleMacros(product.nutritionPer100g, q / 100);
  }
  // Tablespoon/teaspoon/cup → ml fallback when product is liquid.
  const ML_MAP = { tbsp: 15, tsp: 5, cup: 250 };
  if (ML_MAP[u] != null && su === "ml") {
    const factor = (q * ML_MAP[u]) / (product.servingSize || 1);
    return scaleMacros(product.nutritionPerServe, factor);
  }
  // Best-effort fallback: treat quantity as servings.
  return scaleMacros(product.nutritionPerServe, q);
}

function scaleMacros(per, factor) {
  if (!per) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  return {
    calories: +((per.calories || 0) * factor).toFixed(0),
    protein:  +((per.protein  || 0) * factor).toFixed(1),
    carbs:    +((per.carbs    || 0) * factor).toFixed(1),
    fat:      +((per.fat      || 0) * factor).toFixed(1),
  };
}

/* Sanity-check a product's nutrition block before displaying it. Returns
   { ok: boolean, warnings: string[] }. Never throws. */
export function validateProductNutrition(product) {
  const warnings = [];
  if (!product) return { ok: false, warnings: ["No product"] };
  const n = product.nutritionPerServe || {};
  const numericKeys = ["calories", "protein", "carbs", "fat"];
  numericKeys.forEach(k => {
    if (n[k] != null && (typeof n[k] !== "number" || Number.isNaN(n[k]))) {
      warnings.push(`${k} is not numeric`);
    }
  });
  if (!product.servingSize || !product.servingUnit) {
    warnings.push("Missing serving size / unit");
  }
  return { ok: warnings.length === 0, warnings };
}

/* Future hook for live camera-frame decoding. Currently a clean placeholder
   that always returns null — IngredientScannerModal falls back to upload /
   manual entry until a safe in-browser decoder is added. */
// eslint-disable-next-line no-unused-vars
export async function decodeBarcodeFromImage(_imageDataUrlOrBlob) {
  return null;
}
